"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import {
  AlertCircle,
  BookOpen,
  CheckCircle2,
  ExternalLink,
  Loader2,
  SearchCheck,
  Sparkles,
  Timer,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { encodeId } from "@/lib/id-encoder";
import { secureGet, securePost } from "@/lib/security/fetch-client";
import { trackAiBriefImpression, trackAiBriefRequest, trackAiBriefSourceClick } from "@/lib/analytics";
import { ScreenshotGallery } from "@/components/screenshot-gallery";
import type { PublicTopicBrief } from "@/lib/ai-brief/types";
import { isActiveBriefStatus, isDisplayableBriefStatus } from "@/lib/ai-brief/types";
import { estimateBriefProgress } from "@/lib/ai-brief/progress";
import { useAuth } from "@/components/auth-provider";
import { useAuthDialog } from "@/components/auth-dialog-provider";

interface TopicSummaryProps {
  topicId: number;
  topicName: string;
  rankHistory?: Array<{ timestamp: Date; rank: number; hotindex: number }>;
  /** 关闭父级 Sheet，用于在打开 auth dialog 前解除 Radix 焦点锁定 */
  onCloseDrawer?: () => void;
  /** 登录成功后重新打开 Sheet */
  onReopenDrawer?: () => void;
}

interface BriefResponse {
  brief: PublicTopicBrief | null;
}

function formatDate(value: string | null): string {
  if (!value) return "";
  return new Date(value).toLocaleString("zh-CN", {
    month: "numeric",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function getBriefPrompt(rankHistory?: TopicSummaryProps["rankHistory"]) {
  if (!rankHistory || rankHistory.length === 0) {
    return {
      title: "这条热搜还缺一份可靠解读",
      reason: "直接刷讨论容易被碎片信息带着走，先用公开来源把来龙去脉梳理清楚。",
      signal: "适合补齐背景",
    };
  }
  const bestRank = Math.min(...rankHistory.map((item) => item.rank));
  const maxHot = Math.max(...rankHistory.map((item) => item.hotindex));
  if (bestRank <= 2) {
    return {
      title: "榜首级热点，建议先看清事实线",
      reason: "排名越靠前，信息密度和情绪噪音越高。生成解读后会把已确认事实、争议点和来源分开。",
      signal: `最高第 ${bestRank} 名 · 热度 ${maxHot >= 10000 ? `${(maxHot / 10000).toFixed(1)}万` : maxHot}`,
    };
  }
  if (rankHistory.length >= 6) {
    return {
      title: "持续在榜的话题，值得留一份事件档案",
      reason: "持续出现通常代表事件仍在扩散。生成后下次再打开会直接复用，不需要重复检索。",
      signal: `${rankHistory.length} 个时间点有记录`,
    };
  }
  return {
    title: "短时热点，适合用 30 秒判断值不值得追",
    reason: "先让 AI 汇总公开资料，再决定要不要继续看趋势、来源和微博现场。",
    signal: `最高第 ${bestRank} 名`,
  };
}

export function TopicSummary({ topicId, topicName, rankHistory, onCloseDrawer, onReopenDrawer }: TopicSummaryProps) {
  const { user, loading: authLoading } = useAuth();
  const { openAuth } = useAuthDialog();
  const [brief, setBrief] = useState<PublicTopicBrief | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progressClock, setProgressClock] = useState(() => Date.now());
  const [partialContent, setPartialContent] = useState<{ headline?: string; brief?: string; reasoning?: string } | null>(null);
  const [streamStage, setStreamStage] = useState<string | null>(null);
  const [streamSources, setStreamSources] = useState<Array<{ id: number; siteName: string; title: string }> | null>(null);
  const trackedBriefIdRef = useRef<string | number | null>(null);

  const loadBrief = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const response = await secureGet<BriefResponse>("/api/ai/brief", {
        topicId: encodeId(topicId),
      });
      setBrief(response.brief);
      setError(null);
      if (
        response.brief
        && isDisplayableBriefStatus(response.brief.status)
        && response.brief.content
        && trackedBriefIdRef.current !== response.brief.id
      ) {
        trackedBriefIdRef.current = response.brief.id;
        trackAiBriefImpression(topicName, response.brief.sourceCount, response.brief.status);
      }
    } catch {
      if (!quiet) setError("事件解读暂时无法加载，请稍后重试。");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [topicId]);

  useEffect(() => {
    setBrief(null);
    setError(null);
    void loadBrief();
  }, [loadBrief]);

  useEffect(() => {
    if (!brief || !isActiveBriefStatus(brief.status)) return;
    const timer = window.setInterval(() => void loadBrief(true), 1_500);
    return () => window.clearInterval(timer);
  }, [brief, loadBrief]);

  useEffect(() => {
    if (!brief || !isActiveBriefStatus(brief.status)) return;
    setProgressClock(Date.now());
    const timer = window.setInterval(() => setProgressClock(Date.now()), 500);
    return () => window.clearInterval(timer);
  }, [brief?.id, brief?.status, brief?.progress.stage, brief?.updatedAt]);

  // SSE 流式连接：用户触发的 brief 走流式，实时推送 headline/brief
  const [streamBriefId, setStreamBriefId] = useState<string | null>(null);

  useEffect(() => {
    if (!streamBriefId) {
      setPartialContent(null);
      setStreamStage(null);
      setStreamSources(null);
      return;
    }

    const encoded = encodeId(topicId);
    const es = new EventSource(`/api/ai/brief/stream?topicId=${encoded}&briefId=${streamBriefId}`);

    es.addEventListener("status", (e) => {
      try {
        const { stage } = JSON.parse(e.data);
        setStreamStage(stage);
      } catch { /* ignore */ }
    });

    es.addEventListener("sources", (e) => {
      try {
        const data = JSON.parse(e.data);
        setStreamSources(data.sources || []);
      } catch { /* ignore */ }
      setStreamStage("generating");
    });

    es.addEventListener("partial", (e) => {
      try {
        const { field, value } = JSON.parse(e.data);
        // 防护：不显示原始 JSON（如果 LLM 返回格式异常）
        if (typeof value === "string" && !value.startsWith("{") && !value.startsWith("[")) {
          setPartialContent((prev) => ({ ...prev, [field]: value }));
        }
      } catch { /* ignore */ }
    });

    es.addEventListener("done", (e) => {
      try {
        const { brief: done } = JSON.parse(e.data);
        if (done) {
          setBrief(done);
          setPartialContent(null);
          setStreamStage(null);
          setStreamSources(null);
        }
      } catch { /* ignore */ }
      es.close();
      setStreamBriefId(null);
    });

    es.onerror = () => {
      es.close();
      setStreamBriefId(null);
      setPartialContent(null);
      setStreamStage(null);
      setStreamSources(null);
    };

    return () => {
      es.close();
    };
  }, [streamBriefId, topicId]);

  const requestBrief = async () => {
    setSubmitting(true);
    setError(null);
    setPartialContent(null);
    setStreamSources(null);
    try {
      const response = await securePost<BriefResponse & { briefId?: string }>("/api/ai/brief", {
        topicId: encodeId(topicId),
      });
      setBrief(response.brief);
      trackAiBriefRequest(topicName);
      // 启动 SSE 流式连接
      if (response.briefId && response.brief?.status !== "succeeded") {
        setStreamBriefId(response.briefId);
      }
    } catch (requestError) {
      const message = requestError instanceof Error ? requestError.message : "";
      setError(message.includes("429")
        ? "今日 AI 使用次数已用完，请明天再试。"
        : "创建解读任务失败，请稍后重试。");
    } finally {
      setSubmitting(false);
    }
  };

  const openLoginForBrief = () => {
    // Radix Sheet (TopicDrawer) 的 modal 焦点锁定会阻止 auth dialog 输入框获取焦点。
    // 必须先关闭 Sheet，再打开 auth dialog。
    onCloseDrawer?.();
    // 延迟一帧确保 Sheet 关闭动画开始后再打开 auth dialog
    requestAnimationFrame(() => {
      openAuth({
        mode: "login",
        title: "登录后生成事件解读",
        description: `登录后将为「${topicName}」检索并核对公开资料。`,
        icon: <BookOpen className="h-5 w-5 text-violet-600" />,
        onSuccess: () => {
          // 登录成功：重新打开 Sheet 并触发解读生成
          onReopenDrawer?.();
          // 延迟确保 Sheet 渲染完成后再触发解读
          setTimeout(() => void requestBrief(), 300);
        },
      });
    });
  };

  const content = brief?.content;
  const sourceMap = new Map(brief?.sources.map((source) => [source.id, source]) ?? []);
  const estimatedProgress = brief ? estimateBriefProgress(brief, progressClock) : null;
  const emptyPrompt = getBriefPrompt(rankHistory);

  return (
    <Card data-testid="ai-event-brief">
      <CardHeader className="pb-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <CardTitle className="flex items-center gap-2 text-lg">
            <BookOpen className="h-5 w-5 text-violet-600" />
            AI 事件解读
          </CardTitle>
          {brief && isDisplayableBriefStatus(brief.status) && (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <CheckCircle2 className="h-4 w-4 text-emerald-600" />
              {brief.qualityScore <= 10
                ? "公开资料有限，已保留核心判断"
                : `基于 ${brief.sourceCount} 个网页来源`}
            </div>
          )}
        </div>
      </CardHeader>

      <CardContent>
        {loading && (
          <div className="flex items-center rounded-lg bg-muted/50 px-4 py-5 text-sm text-muted-foreground">
            <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            正在读取已有解读…
          </div>
        )}

        {!loading && !brief && !error && (
          <div className="overflow-hidden rounded-xl border border-violet-200 bg-gradient-to-br from-violet-50 via-background to-amber-50 p-5 dark:border-violet-900 dark:from-violet-950/20 dark:via-background dark:to-amber-950/20">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div className="min-w-0">
                <Badge variant="secondary" className="mb-3 bg-white/80 text-violet-700 dark:bg-violet-950/50 dark:text-violet-200">
                  {emptyPrompt.signal}
                </Badge>
                <p className="text-base font-semibold">{emptyPrompt.title}</p>
                <p className="mt-2 text-sm leading-6 text-muted-foreground">
                  {emptyPrompt.reason}
                </p>
              </div>
              <div className="grid shrink-0 grid-cols-3 gap-2 text-center sm:w-56">
                <div className="rounded-lg border bg-background/80 px-2 py-2">
                  <SearchCheck className="mx-auto h-4 w-4 text-violet-600" />
                  <div className="mt-1 text-[11px] text-muted-foreground">查来源</div>
                </div>
                <div className="rounded-lg border bg-background/80 px-2 py-2">
                  <CheckCircle2 className="mx-auto h-4 w-4 text-emerald-600" />
                  <div className="mt-1 text-[11px] text-muted-foreground">分事实</div>
                </div>
                <div className="rounded-lg border bg-background/80 px-2 py-2">
                  <Timer className="mx-auto h-4 w-4 text-amber-600" />
                  <div className="mt-1 text-[11px] text-muted-foreground">省时间</div>
                </div>
              </div>
            </div>
            {user ? (
              <Button className="mt-4" onClick={requestBrief} disabled={submitting}>
                {submitting ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Sparkles className="mr-2 h-4 w-4" />
                )}
                {submitting ? "正在提交…" : "生成我的事件解读"}
              </Button>
            ) : (
              <Button
                className="mt-4"
                disabled={authLoading}
                onClick={openLoginForBrief}
              >
                {authLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />}
                登录后生成解读
              </Button>
            )}
            <p className="mt-3 text-xs leading-5 text-muted-foreground">
              生成会消耗 1 次今日 AI 额度；完成后公开保存，后续用户可直接查看。
            </p>
          </div>
        )}

        {/* 骨架屏：提交中但还没有 brief 状态 */}
        {submitting && !brief && (
          <div className="space-y-3 rounded-xl border border-violet-100 bg-violet-50/40 p-5 animate-pulse dark:border-violet-900/50 dark:bg-violet-950/10">
            <div className="h-5 w-3/4 rounded-md bg-violet-100 dark:bg-violet-900/30" />
            <div className="h-4 w-full rounded-md bg-muted" />
            <div className="h-4 w-5/6 rounded-md bg-muted" />
          </div>
        )}

        {brief && isActiveBriefStatus(brief.status) && (
          <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-5 dark:border-violet-900 dark:bg-violet-950/20">

            {/* 步骤指示器 */}
            <div className="mb-4 flex items-center gap-0">
              {[
                { key: "searching", label: "检索来源", icon: SearchCheck },
                { key: "generating", label: "生成解读", icon: Sparkles },
              ].map((step, i) => {
                const isActive = streamStage === step.key;
                const isDone = step.key === "searching" && (streamStage === "generating" || !!partialContent?.headline || !!streamSources);
                const Icon = step.icon;
                return (
                  <div key={step.key} className="flex items-center">
                    {i > 0 && (
                      <div className={`mx-2 h-px w-6 transition-all duration-500 ${isDone ? "bg-emerald-400" : "bg-violet-200"}`} />
                    )}
                    <div className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-medium transition-all duration-300 ${
                      isActive ? "bg-violet-100 text-violet-700 shadow-sm shadow-violet-200 dark:bg-violet-900/50 dark:text-violet-300" :
                      isDone ? "bg-emerald-50 text-emerald-600 dark:bg-emerald-950/30 dark:text-emerald-400" :
                      "bg-muted/30 text-muted-foreground"
                    }`}>
                      {isActive ? (
                        <Loader2 className="h-3 w-3 animate-spin" />
                      ) : isDone ? (
                        <CheckCircle2 className="h-3 w-3 animate-in zoom-in duration-300" />
                      ) : (
                        <Icon className="h-3 w-3" />
                      )}
                      {step.label}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* 搜索结果来源卡片 */}
            {streamSources && streamSources.length > 0 && !partialContent?.headline && (
              <div className="mb-4 animate-in fade-in slide-in-from-top-1 duration-300">
                <p className="mb-2.5 text-xs font-medium text-violet-600">
                  已检索到 {streamSources.length} 个来源
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {streamSources.slice(0, 6).map((s) => (
                    <div key={s.id} className="rounded-lg border border-violet-100 bg-white/60 px-3 py-2 dark:border-violet-900/50 dark:bg-violet-950/30">
                      <p className="truncate text-xs font-medium text-foreground/80">{s.siteName}</p>
                      <p className="mt-0.5 truncate text-[11px] text-muted-foreground">{s.title.slice(0, 30)}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* 流式内容：headline/brief 逐字渲染 + 闪烁光标 */}
            {partialContent?.headline && (
              <div className="mb-4 animate-in fade-in slide-in-from-top-1 duration-500">
                <p className="text-base font-semibold leading-7">
                  {partialContent.headline}
                  <span className="ml-0.5 inline-block animate-pulse text-violet-500">▊</span>
                </p>
                {partialContent.brief && (
                  <p className="mt-2 text-sm leading-6 text-foreground/80 animate-in fade-in duration-500">
                    {partialContent.brief}
                    <span className="ml-0.5 inline-block animate-pulse text-violet-500">▊</span>
                  </p>
                )}
              </div>
            )}

            {/* 状态文案 */}
            <p className="text-xs text-muted-foreground">
              {partialContent?.headline
                ? partialContent?.brief
                  ? "核心内容已就绪，正在补充完整分析…"
                  : "标题已就绪，正在生成摘要…"
                : streamSources
                  ? "AI 正在阅读来源并组织解读…"
                  : streamStage === "searching"
                    ? "正在检索相关网页来源…"
                    : "准备中…"}
            </p>
          </div>
        )}

        {/* 无 AI 摘要时：独立展示微博截图 */}
        {!loading && !brief?.content && (
          <div className="not-prose mb-4">
            <ScreenshotGallery topicName={topicName} placeholder={false} />
          </div>
        )}

        {brief && isDisplayableBriefStatus(brief.status) && content && (
          <article className="space-y-6">
            <section>
              <Badge variant="secondary" className="mb-3">
                {brief.qualityScore <= 10 ? "低置信度 · 待补充" : "30 秒看懂"}
              </Badge>
              <h3 className="text-xl font-semibold leading-8">{content.headline}</h3>
              <p className="mt-3 text-[15px] leading-7 text-foreground/85">{content.brief}</p>
            </section>

            {/* 有 AI 摘要时：截图在摘要之后 */}
            <div className="not-prose">
              <ScreenshotGallery topicName={topicName} placeholder={false} />
            </div>

            {content.background.length > 0 && (
              <section className="border-t pt-5">
                <h4 className="font-semibold">事情原委</h4>
                <div className="mt-3 space-y-3">
                  {content.background.map((paragraph, index) => (
                    <p key={index} className="text-sm leading-7 text-foreground/80">{paragraph}</p>
                  ))}
                </div>
              </section>
            )}

            {content.confirmedFacts.length > 0 && (
              <section className="border-t pt-5">
                <h4 className="font-semibold">目前可以确认</h4>
                <div className="mt-3 space-y-4">
                  {content.confirmedFacts.map((fact, index) => (
                    <div key={index} className="rounded-lg bg-muted/45 px-4 py-3">
                      <p className="text-sm leading-6">{fact.text}</p>
                      <div className="mt-2 flex flex-wrap gap-1.5">
                        {fact.sourceIds.map((sourceId) => {
                          const source = sourceMap.get(sourceId);
                          if (!source) return null;
                          return (
                            <a
                              key={sourceId}
                              href={source.url}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={() => trackAiBriefSourceClick(topicName, source.siteName)}
                              className="inline-flex items-center rounded-full border bg-background px-2 py-0.5 text-xs text-muted-foreground hover:text-foreground"
                            >
                              {source.siteName}
                              <ExternalLink className="ml-1 h-3 w-3" />
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {content.uncertainties.length > 0 && (
              <section className="border-t pt-5">
                <h4 className="font-semibold">仍待确认</h4>
                <ul className="mt-3 space-y-2">
                  {content.uncertainties.map((item, index) => (
                    <li key={index} className="flex text-sm leading-6 text-muted-foreground">
                      <span className="mr-2 text-amber-600">•</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {content.whyItMatters && (
              <section className="border-t pt-5">
                <h4 className="font-semibold">为什么值得关注</h4>
                <p className="mt-3 text-sm leading-7 text-foreground/80">{content.whyItMatters}</p>
              </section>
            )}

            {brief.sources.length > 0 && (
              <details className="border-t pt-5">
                <summary className="cursor-pointer text-sm font-medium">
                  查看全部信息来源（{brief.sources.length}）
                </summary>
                <ol className="mt-3 space-y-3">
                  {brief.sources.map((source) => (
                    <li key={source.id} className="text-sm">
                      <a
                        href={source.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-medium hover:underline"
                      >
                        {source.id}. {source.title}
                      </a>
                      <div className="mt-1 text-xs text-muted-foreground">
                        {source.siteName}
                        {source.publishedAt ? ` · ${formatDate(source.publishedAt)}` : ""}
                      </div>
                    </li>
                  ))}
                </ol>
              </details>
            )}

            <p className="border-t pt-4 text-xs text-muted-foreground">
              AI 基于公开网页资料整理，请结合所列来源审慎判断。更新于 {formatDate(brief.generatedAt)}。
            </p>
          </article>
        )}

        {error && (
          <div className="flex items-center rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            <AlertCircle className="mr-2 h-4 w-4" />
            <span className="flex-1">{error}</span>
            <Button variant="ghost" size="sm" onClick={() => void loadBrief()}>
              重试
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
